import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import mixpanel from 'mixpanel-browser';
import * as Sentry from "@sentry/react";
import { dequal } from 'dequal';
import { useRouter } from 'next/router';

import { ERRORS } from '../utils/shared/messages';
import { useAxios } from '../utils/axiosApi';
import { onError } from '../utils/errorLib';
import { doLogout } from '../utils/authLib';
import { userLink } from '../utils/formatAppLib';
import { DEFAULT_FILTERS } from '../utils/materialsLib';
import { useUser } from '../utils/users';
import { useAuth } from '../utils/useAuth';

export const AppContext = React.createContext();

async function beforeUnload(e) {
  await e.preventDefault();
  e.returnValue = '';
}

// This component must wrap every page so that sessions and global UX state are available.
export function AppStateComponent(props) {
  const { isMixPanelReady } = props;

  const { isAuthenticated, user } = useAuth();
  const { logout } = useAuth0();
  const router = useRouter();

  const { apiPost } = useAxios();
  const { userItem, error } = useUser();

  const [ newSession, setNewSession ] = useState(false);
  const [ isAccountLoaded, setIsAccountLoaded ] = useState(false);
  const [ credits, setCredits ] = useState(null);
  const [ serviceCredits, setServiceCredits ] = useState(null);
  const [ discount, setDiscount ] = useState(null);
  const [ skipQa, setSkipQa ] = useState(null);
  const [ qa, setQa ] = useState(null);
  const [ qaDisplay, setQaDisplay ] = useState(null);
  const [ tilingEditorAvailable, setTilingEditorAvailable ] = useState(null);
  const [ userStitchingAvailable, setUserStitchingAvailable ] = useState(null);
  const [ importAvailable, setImportAvailable ] = useState(null);
  const [ galleryUser, setGalleryUser ] = useState(null);
  const [ galleryId, setGalleryId ] = useState(null);
  const [ galleryMaterials, setGalleryMaterials ] = useState([]);
  const [ galleryFrom, setGalleryFrom ] = useState('/home');
  const [ reportFrom, setReportFrom ] = useState('/home');
  const [ welcomeFrom, setWelcomeFrom ] = useState(null);
  const [ retainFilters, setRetainFilters ] = useState(false);
  const [ retainFiltersExclude, setRetainFiltersExclude ] = useState([]);
  const [ filters, setFilters ] = useState(DEFAULT_FILTERS);
  const [ filteredMaterials, setFilteredMaterials ] = useState([]);
  const [ folder, setFolder ] = useState('');
  const [ order, setOrder ] = useState('desc');
  const [ orderBy, setOrderBy ] = useState('createdAt');
  const [ selected, setSelected ] = useState([]);
  const [ lastSelected, setLastSelected ] = useState([]);
  const [ userTags, setUserTags ] = useState([]);
  const [ alert, setAlert ] = useState({});
  const [ navWarning, setNavWarningInternal ] = useState(false);
  const [ redirect, setRedirect ] = useState(null);
  const [ galleryViewMode, setGalleryViewMode ] = useState(0);
  const [ listViewOrder, setListViewOrder ] = useState(null);
  const [ listViewOrderBy, setListViewOrderBy ] = useState(null);
  const [ listViewPage, setListViewPage ] = useState(-1);
  const [ welcomeSkipped, setWelcomeSkipped ] = useState(false);
  const [ isCollab, setIsCollab ] = useState(false);
  const [ suppressAlerts, setSuppressAlerts ] = useState(false);

  const collabParam = router.query.collab === 'true';
  const collabParamDefined = router.query.collab !== undefined;

  useEffect(() => {
    if (newSession && isAuthenticated && user && userItem) {
      setNewSession(false);
      mixpanel.identify(userItem.userId);
      mixpanel.track('Login');
      const message = (`:id: User session started: ${userItem.email} (${userLink(userItem.userId)})`);
      // This is an async call, but we don't await because we're in a useEffect.
      // This might fail if the user only just signed up (race condition) but that's OK because staff will
      // also be notified that the new user has signed up.
      apiPost('/notify', { userId: userItem.userId, messageType: 'session', message }, {});
    }
  }, [apiPost, collabParam, isAuthenticated, newSession, user, userItem]);

  useEffect(() => {
    if (isMixPanelReady) {
      mixpanel.register({ 'isCollab': isCollab });
    }
  }, [isCollab, isMixPanelReady]);

  useEffect(() => {
    if (isAuthenticated && user) {
      Sentry.setUser({
        email: userItem?.email,
        id: user.sub,
      });

      if (userItem) {
        Sentry.setTag('user_email', userItem.email);
      }

      mixpanel.identify(user.sub);

      // HubSpot tracking
      if (userItem?.email) {
        let _hsq = window._hsq = window._hsq || [];
        _hsq.push(['identify', {
          email: userItem.email,
          id: user.sub,
        }]);
      }
  }
  }, [isAuthenticated, user, userItem]);

  useEffect(() => {
    async function actionLogin() {
      if (isAuthenticated && user && userItem) {
        if (!isAccountLoaded) {
          setGalleryUser(userItem.userId);
          setIsAccountLoaded(true);
          setFolder('');
        }

        setQa(userItem.staff);
        setQaDisplay(userItem.staff);

        // Keep in sync with QaToolbar.js galleryUser useEffect
        setCredits(userItem.credits);
        const sc = userItem['creditsService'];
        setServiceCredits(sc ? sc : 0);
        setDiscount(userItem.discount);
        setSkipQa(true);
        setTilingEditorAvailable(true);
        setUserStitchingAvailable(true);
        setImportAvailable(true);

        mixpanel.people.set({
          '$email': userItem.email,
          '$name': userItem.name,
          'Staff': userItem.staff ?? false,
          'Credits': userItem.credits,
          'Service Credits': userItem.creditsService ?? 0,
        });
      }
    }

    actionLogin();
  }, [isAccountLoaded, isAuthenticated, user, userItem]);

  useEffect(() => {
    if (error) {
      onError(error);
      setIsAccountLoaded(false);

      // Check if this is a prod-beta access denied error
      const isProdBeta = process.env.NEXT_PUBLIC_STAGE_API === 'prod-beta';
      const errorData = error?.response?.data?.data;
      const isAccessDenied = errorData?.userId === ERRORS.PROD_BETA_STAFF_ONLY;

      if (isProdBeta && isAccessDenied) {
        showAlert({
          status: 'critical',
          message: `${ERRORS.PROD_BETA_STAFF_ONLY}. You will be logged out.`
        });
        // Log the user out after a short delay to allow them to see the message
        setTimeout(() => {
          doLogout(logout);
        }, 5000);
        return; // Prevent other error handling
      }

      showAlert({ status: 'warning', message: 'An error occurred while fetching account data' });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  useEffect(() => {
    if (collabParamDefined) {
      setIsCollab(collabParam);
    }
    // If collabParam is undefined, do not change isCollab, so it persists for the session
  }, [collabParam, collabParamDefined]);

  // Alert rate limit in ms. Any alerts arriving faster than the rate limit are ignored.
  const ALERT_RATE_LIMIT = 1000;
  const debounceAlertTimestamp = useRef(0);
  const debounceAlertValue = useRef({ });

  const showAlert = useCallback(async (value={ }) => {
    const isCritical = value.status === 'critical';

    // Critical alerts freeze all future alerts
    if (isCritical) {
      setSuppressAlerts(true);
    }

    // Block all alerts if a critical alert has been shown
    if (suppressAlerts && !isCritical) {
      return;
    }

    const nowTimestamp = Date.now();
    const isError = value.status === 'error';
    const lastWasWarning = debounceAlertValue.current.status === 'warning';

    // Critical always shows, errors overwrite warnings, otherwise debounce
    if (
      value.status && (
        isCritical || // Critical bypasses all debouncing
        (isError && lastWasWarning) || // Error overrides warning immediately
        (nowTimestamp > debounceAlertTimestamp.current + ALERT_RATE_LIMIT) ||
        !dequal(value, debounceAlertValue.current)
      )
    ) {
      debounceAlertTimestamp.current = nowTimestamp;
      debounceAlertValue.current = value;

      if (value.status == 'error' || value.status == 'critical') {
        onError({ message: `User alert ${value.status}: ${value.message}` });
      }

      const annotatedValue = {
        ...value,
        open: true,
        timestamp: nowTimestamp,
       };
      setAlert(annotatedValue);
    }
  }, [suppressAlerts]);

  async function setNavWarning(value) {
    const action = value ? window.addEventListener : window.removeEventListener;
    action('beforeunload', beforeUnload);
    setNavWarningInternal(value);
  }

  return (
    <AppContext.Provider value={{
      newSession, setNewSession,
      isAccountLoaded, setIsAccountLoaded,
      credits, setCredits,
      serviceCredits, setServiceCredits,
      discount, setDiscount,
      skipQa, setSkipQa,
      qa, setQa,
      qaDisplay, setQaDisplay,
      tilingEditorAvailable, setTilingEditorAvailable,
      userStitchingAvailable, setUserStitchingAvailable,
      importAvailable, setImportAvailable,
      galleryUser, setGalleryUser,
      galleryMaterials, setGalleryMaterials,
      galleryFrom, setGalleryFrom,
      reportFrom, setReportFrom,
      welcomeFrom, setWelcomeFrom,
      galleryId, setGalleryId,
      retainFilters, setRetainFilters,
      retainFiltersExclude, setRetainFiltersExclude,
      filters, setFilters,
      filteredMaterials, setFilteredMaterials,
      folder, setFolder,
      order, setOrder,
      orderBy, setOrderBy,
      selected, setSelected,
      lastSelected, setLastSelected,
      userTags, setUserTags,
      alert, showAlert,
      navWarning, setNavWarning,
      redirect, setRedirect,
      galleryViewMode, setGalleryViewMode,
      listViewOrder, setListViewOrder,
      listViewOrderBy, setListViewOrderBy,
      listViewPage, setListViewPage,
      welcomeSkipped, setWelcomeSkipped,
      isCollab, setIsCollab,
    }}>
      {props.children}
    </AppContext.Provider>
  );
}
